import type { DateTimeFormatOptions } from 'next-intl';
import { cva, cx } from 'class-variance-authority';
import dayjs from 'dayjs';
import { useFormatter, useNow, useTranslations } from 'next-intl';
import Image from 'next/image';

import { Avatar, cardStyles, LinkBox, LinkOverlay } from '@zealy/design-system';
import { CalendarEventLine, CheckCircleLine, GiftLine } from '@zealy/icons';

import type { SprintCardProps } from './SprintCard.types';
import { SprintRewardStack } from './components/RewardStack';

const TEN_SECONDS = 10000;
const DATETIME_OPTIONS: DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: true,
};

const styles = cva(['flex items-center gap-150 p-300 select-none'], {
  variants: {
    isClickable: {
      true: 'transition-transform hover:scale-[1.01] duration-200 ease-out',
    },
  },
});

export const SprintCard = ({
  className,
  title,
  communityName,
  communityAvatarImgSrc,
  startDate,
  endDate,
  rewardZone = 0,
  sprintImgSrc,
  onClick,
  rewards,
}: SprintCardProps) => {
  const startDateIsValid = !!startDate && dayjs(startDate).isValid();
  const endDateIsValid = !!endDate && dayjs(endDate).isValid();

  const hasGuaranteedReward = rewards?.some(reward => Number(reward.value) > 0);

  const format = useFormatter();
  const t = useTranslations('sprints');
  const mountDate = useNow();
  const updateInterval =
    startDateIsValid && dayjs(startDate).diff(mountDate, 'seconds') <= 180
      ? TEN_SECONDS
      : undefined;
  const now = useNow({
    updateInterval,
  });

  const startDateLabel = startDateIsValid
    ? format.dateTime(startDate, DATETIME_OPTIONS)
    : undefined;
  const endDateLabel = endDateIsValid ? format.dateTime(endDate, DATETIME_OPTIONS) : undefined;

  // Determine if sprint has started
  const hasStarted = startDateIsValid && dayjs(startDate).isBefore(now);
  const hasEnded = endDateIsValid && dayjs(endDate).isBefore(now);

  // Generate relative time label
  const relativeTimeLabel =
    !hasEnded &&
    (hasStarted && endDateIsValid
      ? format.relativeTime(endDate, now)
      : startDateIsValid
      ? format.relativeTime(startDate, now)
      : undefined);

  // Generate calendar display text
  const calendarDisplayText =
    !hasEnded &&
    (hasStarted && endDateIsValid
      ? `ends ${relativeTimeLabel}`
      : startDateIsValid
      ? `starts ${relativeTimeLabel}`
      : t('placeholders.unspecified-time'));

  const rewardsLabel = Math.max(rewardZone ?? 0, 0)
    ? t('rewards', { count: rewardZone ?? 0 })
    : undefined;
  const titleLabel = title
    ? title
    : t('placeholders.sprint-title', { communityName: communityName ?? '' });
  const communityNameLabel = communityName ? communityName : t('placeholders.unknown-community');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === 'Enter' && onClick) {
      onClick();
    }
  };

  return (
    <LinkBox
      className={cx(
        styles({ isClickable: !!onClick }),
        cardStyles({ variant: 'default', isClickable: !!onClick }),
        'gap-300',
        className,
      )}
    >
      {sprintImgSrc && (
        <div
          className={cx(
            'shrink-0 w-[90px] h-[90px] relative',
            hasGuaranteedReward ? 'hidden md:block' : '',
          )}
        >
          <Image
            className="w-full h-full object-cover rounded-xl"
            alt=""
            src={sprintImgSrc}
            width={90}
            height={90}
          />
        </div>
      )}
      <div className="flex flex-col flex-grow self-start gap-200 justify-between min-w-0">
        <span className="heading-3 truncate">{titleLabel}</span>
        <div className="flex flex-col gap-100 text-secondary body-md">
          <span className="flex gap-100 items-center">
            <Avatar
              className="flex-shrink-0 pointer-events-none"
              tabIndex={-1}
              size="xxs"
              name={communityName}
              src={communityAvatarImgSrc}
            />
            <span className="truncate">
              {t('by-community', { communityName: communityNameLabel })}
            </span>
          </span>
          <span className="flex gap-100 items-center">
            <CalendarEventLine className="flex-shrink-0" size={16} />
            <span className="truncate">
              {hasEnded ? (
                startDateIsValid && endDateIsValid ? (
                  <>
                    <time dateTime={startDate.toISOString()}>{startDateLabel}</time>
                    {' – '}
                    <time dateTime={endDate.toISOString()}>{endDateLabel}</time>
                  </>
                ) : (
                  t('placeholders.unspecified-time')
                )
              ) : (
                calendarDisplayText
              )}
            </span>
          </span>
          {hasGuaranteedReward && (
            <span className="flex gap-100 items-center">
              <CheckCircleLine className="flex-shrink-0" size={16} />
              <span className="truncate">Rewards secured by Zealy</span>
            </span>
          )}
          {rewardsLabel && (!rewards || rewards.length === 0) ? (
            <span className="flex gap-100 items-center">
              <GiftLine className="flex-shrink-0" size={16} />
              <span className="truncate">{rewardsLabel}</span>
            </span>
          ) : null}
        </div>
      </div>
      {rewards && rewards.length > 0 && <SprintRewardStack rewards={rewards} />}
      {onClick ? (
        <LinkOverlay
          As="div"
          role="button"
          aria-label={titleLabel}
          tabIndex={0}
          onClick={onClick}
          onKeyDown={handleKeyDown}
        />
      ) : null}
    </LinkBox>
  );
};
